import {
  humanStepOutputs,
  RUNBOOK_HUMAN_STEP_OUTPUTS,
  type RunbookStepDTO,
} from '@deepguide-ai/types'

/**
 * One value a step produces and exposes downstream.
 *
 * `label` is what a human reads; `ref` is the canonical `@step.<id>.<name>`
 * form the resolver and the @-menu use. They differ for human steps, where the
 * output name is a prefixed field id (`s2:notes`) and the label is the field's
 * own caption ("Notes").
 */
export interface RunbookStepOutput {
  label: string
  ref: string
}

/**
 * What a step produces and exposes downstream as `@step.<id>.<name>`.
 *
 * Two consumers, deliberately sharing one implementation: the graph step card's
 * Outputs row (renders `label`) and the chat plan card's per-step outputs row
 * (renders `ref` as a pill). Keeping them on one function is what stops the two
 * surfaces from disagreeing about what a step produces.
 *
 * A human step's outputs come from its form — fields in order, then the
 * decision and its reason — which is the same order `humanStepOutputs` emits,
 * so the two zip 1:1. Other kinds expose their authored `outputVar` names,
 * which are already both the label and the ref name.
 */
export function stepOutputs(step: RunbookStepDTO): RunbookStepOutput[] {
  const ref = (name: string) => `@step.${step.id}.${name}`

  if (step.kind === 'human') {
    const labels = (step.human?.fields ?? []).map(
      (field) => field.label || 'Field'
    )
    if (step.human?.decision) {
      labels.push(step.human.decision.label || 'Decision', 'Reason')
    }
    const names = step.human ? humanStepOutputs(step.human) : []
    return labels.map((label, index) => ({
      label,
      // Fall back to the label when the form has no matching descriptor — a
      // partially-authored draft form can be shorter than its label list.
      ref: ref(names[index]?.name ?? label),
    }))
  }

  const names = Array.from(
    new Set([
      ...(step.outputVarNames ?? []),
      ...Object.keys(step.outputs ?? {}),
      ...(step.automated?.actions ?? [])
        .map((action) => action.outputVar?.trim())
        .filter((name): name is string => !!name),
      ...(RUNBOOK_HUMAN_STEP_OUTPUTS[step.kind] ?? []).map(
        (output) => output.name
      ),
    ])
  )
  return names.map((name) => ({ label: name, ref: ref(name) }))
}

/**
 * Label-only view of {@link stepOutputs}, for surfaces that render plain chips
 * rather than ref pills.
 */
export function stepOutputLabels(step: RunbookStepDTO): string[] {
  return stepOutputs(step).map((output) => output.label)
}
